import { Button, TextField } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import { subjectsApi } from 'api/subjects-api';
import { optionSchema } from 'shared/helpers';
import { modalStyles } from 'shared/styles';
import { Option } from 'shared/types';

interface Props {
  isOpen: boolean;
  close: () => void;
  option: Option | null;
  onReset: () => void;
}

export const AddEditOptionModal = ({ isOpen, close, option, onReset }: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const isEditingForm = !!option;

  const formik = useFormik({
    initialValues: {
      name: option?.name || '',
      ruName: option?.ruName || '',
    },
    validationSchema: optionSchema,
    enableReinitialize: true,
    onSubmit: async values => {
      setIsLoading(true);

      try {
        isEditingForm
          ? await subjectsApi.updateSubject({ id: option.id, ...values })
          : await subjectsApi.addSubject(values);
      } catch (error) {
        console.log(error);
        toast.error(t('Something went wrong. Try again!'));
      }

      onReset();
      formik.resetForm();
      setIsLoading(false);
      close();
      toast.success(isEditingForm ? t('Successfully edited!') : t('Successfully added!'));
    },
  });

  const hasFormikErrors = !!Object.keys(formik.errors).length;
  const isSubmitButtonDisabled = hasFormikErrors || isLoading;

  return (
    <Modal isOpen={isOpen} onRequestClose={close} style={modalStyles} closeTimeoutMS={300}>
      <h2>{isEditingForm ? 'Edit' : 'Add'} option</h2>

      <FormikProvider value={formik}>
        <Form className="flex flex-col gap-4">
          <TextField
            id="name"
            label="Name"
            variant="outlined"
            value={formik.values.name}
            onChange={event => formik.setFieldValue('name', event.target.value)}
            error={!!formik.errors.name}
            helperText={formik.errors.name}
          />

          <TextField
            id="nameRu"
            label="Russian Name"
            variant="outlined"
            value={formik.values.ruName}
            onChange={event => formik.setFieldValue('ruName', event.target.value)}
            error={!!formik.errors.ruName}
            helperText={formik.errors.ruName}
          />

          <Button className="btn clear-filter with-icon" type="submit" disabled={isSubmitButtonDisabled}>
            {isEditingForm ? 'Edit' : 'Add'}
          </Button>
        </Form>
      </FormikProvider>
    </Modal>
  );
};
